import React, { useState, useRef, useEffect, useCallback } from 'react';
import { cn } from '../../utils/cn';
import { chatbotService, ChatbotMessage, ChatbotAction } from '../../services/chatbotService';
import { museService } from '../../services/museService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';

interface ChatBotProps {
  className?: string;
  position?: 'bottom-right' | 'bottom-left';
  defaultOpen?: boolean;
}

const ChatBot: React.FC<ChatBotProps> = ({
  className,
  position = 'bottom-right',
  defaultOpen = false
}) => {
  const { userProfile } = useAuth();
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<ChatbotMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showQuickMenu, setShowQuickMenu] = useState(false);
  const [museAvailable, setMuseAvailable] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hide chatbot on AI chat pages - user is already in the full chat interface
  const isOnAIChatPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/ai-chat');

  // Check M.U.S.E. availability on mount
  useEffect(() => {
    if (isOnAIChatPage) return; // Skip if on AI chat page
    const checkMuse = async () => {
      const health = await museService.checkHealth();
      setMuseAvailable(health?.status === 'healthy');
      if (userProfile?.id) {
        museService.setUser(userProfile.id);
      }
    };
    checkMuse();
  }, [userProfile?.id, isOnAIChatPage]);

  // Initialize with greeting
  useEffect(() => {
    if (isOnAIChatPage) return; // Skip if on AI chat page
    if (isOpen && messages.length === 0) {
      const greeting = chatbotService.getGreeting(
        userProfile?.full_name?.split(' ')[0],
        userProfile?.role
      );
      // Add M.U.S.E. indicator if available
      if (museAvailable) {
        greeting.content = `🎭 **M.U.S.E. AI Connected!**\n\n${greeting.content}`;
      }
      setMessages([greeting]);
    }
  }, [isOpen, userProfile, museAvailable, isOnAIChatPage, messages.length]);

  // Scroll to bottom on new messages
  useEffect(() => {
    if (isOnAIChatPage) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOnAIChatPage]);

  // Focus input when opened
  useEffect(() => {
    if (isOnAIChatPage) return;
    if (isOpen && !isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, isMinimized, isOnAIChatPage]);

  // Don't render anything if we're on the AI chat page
  if (isOnAIChatPage) {
    return null;
  }

  const handleAction = useCallback((action: ChatbotAction) => {
    switch (action.action) {
      case 'navigate':
        // Use window.location for navigation since we're outside Router context
        window.location.href = action.target;
        break;
      case 'link':
        window.open(action.target, '_blank');
        break;
      case 'action':
        if (action.target === 'showProblems') {
          showProblemsMenu();
        } else if (action.target === 'showFAQs') {
          showFAQsMenu();
        }
        break;
    }
  }, []);

  const showProblemsMenu = () => {
    const problems = chatbotService.getTroubleshootingProblems();
    const problemsMessage: ChatbotMessage = {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: '🔧 **Common Problems & Solutions**\n\nSelect your issue below and I\'ll guide you through fixing it:',
      timestamp: new Date(),
      suggestions: problems.slice(0, 4).map(p => p.problem)
    };
    setMessages(prev => [...prev, problemsMessage]);
  };

  const showFAQsMenu = () => {
    const categories = chatbotService.getCategories();
    const faqMessage: ChatbotMessage = {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: '📚 **Help Categories**\n\nWhat would you like to know about?',
      timestamp: new Date(),
      suggestions: categories.slice(0, 4)
    };
    setMessages(prev => [...prev, faqMessage]);
  };

  const handleSendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    // Add user message
    const userMessage: ChatbotMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: content.trim(),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setShowQuickMenu(false);

    let response: ChatbotMessage;

    // Try M.U.S.E. first if available
    if (museAvailable) {
      try {
        const museResponse = await museService.chat(content, {
          userRole: userProfile?.role,
          dashboard: window.location.pathname,
        });

        if (museResponse.success) {
          response = {
            id: `msg-${Date.now()}`,
            role: 'assistant',
            content: museResponse.response,
            timestamp: new Date(),
            suggestions: museResponse.fast_path 
              ? ['Tell me about pricing', 'What\'s the ROI?', 'Campaign strategies']
              : ['Tell me more', 'Another question', 'That helped!'],
          };
          
          // Add confidence indicator for complex queries
          if (!museResponse.fast_path && museResponse.agents_used && museResponse.agents_used > 0) {
            response.content += `\n\n_🧠 Analyzed by ${museResponse.agents_used} AI agents (${museResponse.confidence}% confidence)_`;
          }
        } else {
          // M.U.S.E. failed, fall back to local chatbot
          response = chatbotService.processMessage(content, userProfile?.role);
        }
      } catch (error) {
        console.error('M.U.S.E. error, falling back to local chatbot:', error);
        response = chatbotService.processMessage(content, userProfile?.role);
      }
    } else {
      // M.U.S.E. not available, use local chatbot
      await new Promise(resolve => setTimeout(resolve, 500 + Math.random() * 1000));
      response = chatbotService.processMessage(content, userProfile?.role);
    }

    setMessages(prev => [...prev, response]);
    setIsTyping(false);
  }, [userProfile?.role, museAvailable]);

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(inputValue);
    }
  };

  const toggleOpen = () => {
    setIsOpen(!isOpen);
    setIsMinimized(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const clearChat = () => {
    const greeting = chatbotService.getGreeting(
      userProfile?.full_name?.split(' ')[0],
      userProfile?.role
    );
    setMessages([greeting]);
  };

  const positionClasses = position === 'bottom-right' 
    ? 'right-4 sm:right-6' 
    : 'right-4 sm:right-6';

  return (
    <>
      {/* Chat Widget Button */}
      {!isOpen && (
        <button
          onClick={toggleOpen}
          className={cn(
            'fixed bottom-20 sm:bottom-24 z-[9999] group',
            'flex items-center gap-3 px-4 py-3 rounded-full',
            'bg-gradient-to-r from-red-600 via-red-500 to-red-600 text-white',
            'shadow-lg shadow-red-500/40 hover:shadow-xl hover:shadow-red-500/50',
            'transition-all duration-300 hover:-translate-y-1 hover:scale-105',
            positionClasses,
            className
          )}
          aria-label="Ask M.U.S.E. AI"
        >
          <div className="relative">
            <Icon name="Sparkles" size={24} />
            <span className={cn(
              'absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white',
              museAvailable ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
            )} />
          </div>
          <span className="hidden sm:inline font-medium">Ask M.U.S.E. AI</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className={cn(
            'fixed bottom-20 sm:bottom-24 z-[9999]',
            'w-[calc(100vw-2rem)] sm:w-[400px]',
            isMinimized ? 'h-14' : 'h-[500px] max-h-[80vh]',
            'bg-dark-900 rounded-2xl shadow-2xl border border-dark-700',
            'flex flex-col overflow-hidden',
            'transition-all duration-300 ease-out',
            positionClasses
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-red-600 via-primary to-red-500 text-white rounded-t-2xl">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Icon name="Bot" size={24} />
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-primary" />
              </div>
              <div>
                <h3 className="font-semibold">NOONX Assistant</h3>
                <p className="text-xs text-white/80">Online • Typically replies instantly</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={clearChat}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Clear chat"
              >
                <Icon name="RefreshCw" size={18} />
              </button>
              <button
                onClick={toggleMinimize}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title={isMinimized ? 'Expand' : 'Minimize'}
              >
                <Icon name={isMinimized ? 'Maximize2' : 'Minimize2'} size={18} />
              </button>
              <button
                onClick={toggleOpen}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Close"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          {/* Messages Container */}
          {!isMinimized && (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-dark-700 scrollbar-track-transparent">
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    onSuggestionClick={handleSuggestionClick}
                    onActionClick={handleAction}
                  />
                ))}
                
                {/* Typing indicator */}
                {isTyping && (
                  <div className="flex items-start gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Icon name="Bot" size={16} className="text-primary" />
                    </div>
                    <div className="bg-dark-800 rounded-2xl rounded-tl-none px-4 py-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="p-4 border-t border-dark-700 bg-dark-800/50">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowQuickMenu(!showQuickMenu)}
                    className={cn(
                      'p-2.5 rounded-xl transition-all duration-200',
                      showQuickMenu 
                        ? 'bg-primary text-white'
                        : 'bg-dark-700 text-gray-400 hover:text-white hover:bg-dark-600'
                    )}
                    title="Quick actions"
                  >
                    <Icon name={showQuickMenu ? 'X' : 'Zap'} size={20} />
                  </button>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Describe your problem or ask a question..."
                    className="flex-1 bg-dark-800 border border-dark-600 rounded-xl px-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                    disabled={isTyping}
                  />
                  <button
                    onClick={() => handleSendMessage(inputValue)}
                    disabled={!inputValue.trim() || isTyping}
                    className={cn(
                      'p-2.5 rounded-xl transition-all duration-200',
                      inputValue.trim() && !isTyping
                        ? 'bg-primary text-white hover:bg-primary-dark'
                        : 'bg-dark-700 text-gray-500 cursor-not-allowed'
                    )}
                  >
                    <Icon name="Send" size={20} />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  Press Enter to send • <button onClick={clearChat} className="text-primary hover:underline">Clear chat</button>
                </p>
              </div>

              {/* Quick Actions Panel */}
              {showQuickMenu && (
                <div className="p-3 border-t border-dark-700 bg-dark-800/80">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={showProblemsMenu}
                      className="flex items-center gap-2 p-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm text-white transition-colors"
                    >
                      <Icon name="AlertCircle" size={16} className="text-orange-400" />
                      <span>Report Problem</span>
                    </button>
                    <button
                      onClick={showFAQsMenu}
                      className="flex items-center gap-2 p-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm text-white transition-colors"
                    >
                      <Icon name="HelpCircle" size={16} className="text-blue-400" />
                      <span>Browse FAQs</span>
                    </button>
                    <button
                      onClick={() => { handleSendMessage('How do payments work?'); setShowQuickMenu(false); }}
                      className="flex items-center gap-2 p-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm text-white transition-colors"
                    >
                      <Icon name="CreditCard" size={16} className="text-green-400" />
                      <span>Payments</span>
                    </button>
                    <button
                      onClick={() => { handleSendMessage('How do I contact support?'); setShowQuickMenu(false); }}
                      className="flex items-center gap-2 p-2 bg-dark-700 hover:bg-dark-600 rounded-lg text-sm text-white transition-colors"
                    >
                      <Icon name="Mail" size={16} className="text-purple-400" />
                      <span>Contact Support</span>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

// Message Bubble Component
interface MessageBubbleProps {
  message: ChatbotMessage;
  onSuggestionClick?: (suggestion: string) => void;
  onActionClick?: (action: ChatbotAction) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onSuggestionClick, onActionClick }) => {
  const isUser = message.role === 'user';

  return (
    <div className={cn('flex items-start gap-2', isUser && 'flex-row-reverse')}>
      {/* Avatar */}
      <div className={cn(
        'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
        isUser ? 'bg-primary/20' : 'bg-primary/20'
      )}>
        <Icon 
          name={isUser ? 'User' : 'Bot'} 
          size={16} 
          className={isUser ? 'text-primary' : 'text-primary'} 
        />
      </div>

      {/* Message Content */}
      <div className={cn('max-w-[80%] space-y-2')}>
        <div className={cn(
          'rounded-2xl px-4 py-3',
          isUser 
            ? 'bg-primary text-white rounded-tr-none' 
            : 'bg-dark-800 text-white rounded-tl-none border border-dark-700'
        )}>
          <div className="text-sm whitespace-pre-wrap leading-relaxed">
            {formatMessage(message.content)}
          </div>
        </div>

        {/* Action Buttons */}
        {!isUser && message.actions && message.actions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {message.actions.map((action, idx) => (
              <button
                key={idx}
                onClick={() => onActionClick?.(action)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 rounded-full transition-colors"
              >
                {action.icon && <Icon name={action.icon as any} size={12} />}
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Suggestions */}
        {!isUser && message.suggestions && message.suggestions.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {message.suggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => onSuggestionClick?.(suggestion)}
                className="text-xs px-3 py-1.5 bg-dark-800 hover:bg-dark-700 text-gray-300 hover:text-white border border-dark-600 rounded-full transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp */}
        <p className={cn(
          'text-xs text-gray-500',
          isUser && 'text-right'
        )}>
          {new Date(message.timestamp).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </p>
      </div>
    </div>
  );
};

// Format message content with basic markdown support
function formatMessage(content: string): React.ReactNode {
  // Split by bold markers
  const parts = content.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    
    // Handle bullet points
    if (part.includes('•')) {
      const lines = part.split('\n');
      return lines.map((line, lineIndex) => (
        <React.Fragment key={`${index}-${lineIndex}`}>
          {line}
          {lineIndex < lines.length - 1 && <br />}
        </React.Fragment>
      ));
    }
    
    return part;
  });
}

export default ChatBot;
